import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home,
  TrendingUp,
  Coins,
  DollarSign,
  BarChart3,
  Brain,
  Briefcase,
  Newspaper,
  Search,
  Building2,
  Globe,
  Settings,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Layers,
  Wrench,
  Star,
  Bot,
  Lock
} from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

const Sidebar = ({ mobileOpen = false, onClose }) => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();

  // Version check - force reset to collapsed state for clean look
  useEffect(() => {
    const sidebarVersion = localStorage.getItem('sidebarVersion');
    if (sidebarVersion !== '2.0') {
      localStorage.setItem('assetsExpanded', 'false');
      localStorage.setItem('investmentToolsExpanded', 'false');
      localStorage.setItem('sidebarVersion', '2.0');
    }
  }, []);

  // Initialize with localStorage or default to FALSE (collapsed for clean look)
  const [assetsExpanded, setAssetsExpanded] = useState(() => {
    const saved = localStorage.getItem('assetsExpanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  
  const [investmentToolsExpanded, setInvestmentToolsExpanded] = useState(() => {
    const saved = localStorage.getItem('investmentToolsExpanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  
  const [aboutEraVisible, setAboutEraVisible] = useState(() => {
    const saved = localStorage.getItem('aboutEraVisible');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem('assetsExpanded', JSON.stringify(assetsExpanded));
  }, [assetsExpanded]);

  useEffect(() => {
    localStorage.setItem('investmentToolsExpanded', JSON.stringify(investmentToolsExpanded));
  }, [investmentToolsExpanded]);

  useEffect(() => {
    localStorage.setItem('aboutEraVisible', JSON.stringify(aboutEraVisible));
  }, [aboutEraVisible]);

  // Listen for custom events to toggle sidebar sections
  useEffect(() => {
    const handleToggleAssets = () => {
      setAssetsExpanded(prev => !prev);
    };
    
    const handleToggleInvestmentTools = () => {
      setInvestmentToolsExpanded(prev => !prev);
    };

    window.addEventListener('toggleAssets', handleToggleAssets);
    window.addEventListener('toggleInvestmentTools', handleToggleInvestmentTools);

    return () => {
      window.removeEventListener('toggleAssets', handleToggleAssets);
      window.removeEventListener('toggleInvestmentTools', handleToggleInvestmentTools);
    };
  }, []);

  const navigationStructure = {
    main: [
      { path: '/', icon: Home, label: 'Cockpit', exact: true, tooltip: 'Your 2-minute market snapshot' },
    ],
    assets: {
      label: 'Assets',
      icon: Layers,
      items: [
        { path: '/crypto', icon: Coins, label: 'Crypto', tooltip: 'Track cryptocurrency markets' },
        { path: '/equities', icon: TrendingUp, label: 'Equities', tooltip: 'Monitor stock markets' },
        { path: '/commodities', icon: DollarSign, label: 'Commodities', tooltip: 'Gold, Silver, Oil markets' },
        { path: '/screener', icon: Search, label: 'Screener', tooltip: 'Find investment opportunities' },
      ]
    },
    investmentTools: {
      label: 'Investment Tools',
      icon: Wrench,
      premium: false,
      items: [
        { path: '/macro', icon: Globe, label: 'Macro Dashboard', premium: false, tooltip: 'Economic indicators & policy analysis' },
        { path: '/sentiment', icon: BarChart3, label: 'Sentiment', premium: true, featureKey: 'sentiment', tooltip: 'Market sentiment analysis' },
        { path: '/watchlist', icon: Star, label: 'Watchlist', premium: true, featureKey: 'watchlist', tooltip: 'Your personal portfolio tracker' },
        { path: '/thematic-investing', icon: TrendingUp, label: 'Thematic Investing', premium: true, featureKey: 'thematic_investing', tooltip: '14 investment themes tracking' },
        { path: '/premium-intelligence', icon: Sparkles, label: 'Crypto Intel', premium: true, featureKey: 'crypto_intel', tooltip: 'Macro regime & Top 10 crypto alpha' },
        /* News Intel removed — replaced by Community (Twitter feed) */
        { path: '/agents', icon: Bot, label: 'AI Agents', premium: true, featureKey: 'ai_agents', tooltip: 'AI-powered investment assistants' },
        { path: '/single-stocks', icon: BarChart3, label: 'Single Stocks', premium: true, featureKey: 'single_stocks', tooltip: 'Institutional-grade fundamentals', proMax: true },
      ]
    },
    learning: [
      { path: '/curiosity', icon: Brain, label: 'Curiosity', tooltip: 'Learn blockchain & crypto fundamentals' },
      { path: '/settings', icon: Settings, label: 'Settings', tooltip: 'Manage account and preferences' },
      { path: '/corporate', icon: Building2, label: 'Corporate', tooltip: 'Terms & About Era' },
    ]
  };

  return (
    <>
    {mobileOpen && <div className="fixed inset-0 bg-black/40 z-[999] md:hidden" onClick={onClose} data-testid="sidebar-backdrop" />}
    <div onClick={(e) => { if (e.target.closest('a') && onClose) onClose(); }}
      className={`w-64 bg-white border-r border-gray-200 h-screen shadow-sm flex flex-col overflow-visible fixed md:static inset-y-0 left-0 transform transition-transform duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`} style={{ zIndex: 1000 }}>
      {/* Logo - Click to go to landing page */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex flex-col items-center space-y-2">
          <div className="w-full flex justify-center cursor-pointer" onClick={() => navigate('/landing')} title="About Era Digital">
            <img
              src="/era-logo.jpg"
              alt="Era Digital Logo"
              className="w-24 h-24 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              style={{ objectPosition: 'center' }}
            />
          </div>
          <p className="text-gray-body text-sm text-center font-medium">Bridging DeFi and TradFi</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 flex-1 overflow-y-auto overflow-x-visible">
        <ul className="space-y-1 relative">
          {/* Main Navigation */}
          {navigationStructure.main.map((item) => (
            <li key={item.path} className="group relative">
              <NavLink
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-bitcoin-orange text-white shadow-md'
                      : 'text-gray-600 hover:bg-bitcoin-orange hover:text-white hover:shadow-md'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            </li>
          ))}

          {/* Assets Group */}
          <li className="mt-4">
            <button
              onClick={() => setAssetsExpanded(!assetsExpanded)}
              className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-gray-100 text-gray-500 font-semibold text-xs uppercase tracking-wider"
            >
              <navigationStructure.assets.icon className="w-5 h-5 text-bitcoin-orange" />
              <span className="flex-1 text-left">{navigationStructure.assets.label}</span>
              {assetsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {assetsExpanded && (
              <ul className="mt-1 ml-4 space-y-1 border-l-2 border-bitcoin-orange pl-2">
                {navigationStructure.assets.items.map((item) => (
                  <li key={item.path} className="group relative">
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-bitcoin-orange text-white shadow-md'
                            : 'text-gray-600 hover:bg-bitcoin-orange hover:text-white hover:shadow-md'
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Standalone items moved to Investment Tools group */}

          {/* Investment Tools Group */}
          <li className="mt-4">
            <button
              onClick={() => setInvestmentToolsExpanded(!investmentToolsExpanded)}
              className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-orange-50 text-gray-500 font-semibold text-xs uppercase tracking-wider"
            >
              <navigationStructure.investmentTools.icon className="w-5 h-5 text-bitcoin-orange" />
              <span className="flex-1 text-left">{navigationStructure.investmentTools.label}</span>
              {investmentToolsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {investmentToolsExpanded && (
              <ul className="mt-1 ml-4 space-y-1 border-l-2 border-bitcoin-orange pl-2">
                {navigationStructure.investmentTools.items.map((item) => {
                  const isLocked = item.premium && item.featureKey && !hasFeature(item.featureKey);
                  return (
                    <li key={item.path} className="group relative">
                      {isLocked ? (
                        <button
                          onClick={() => navigate('/pricing')}
                          className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 text-gray-400 hover:bg-gray-100 cursor-pointer"
                        >
                          <item.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{item.label}</span>
                          <span className="ml-auto flex items-center space-x-1">
                            <Lock className="w-3 h-3" />
                            <span className={`px-2 py-0.5 text-xs font-bold text-white rounded-full shadow-sm ${item.proMax ? '' : 'bg-bitcoin-orange'}`}
                              style={item.proMax ? { backgroundColor: '#DD524C' } : {}}>
                              {item.proMax ? 'PRO MAX' : 'PRO'}
                            </span>
                          </span>
                        </button>
                      ) : (
                        <NavLink
                          to={item.path}
                          className={({ isActive }) =>
                            `flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                              isActive
                                ? 'bg-bitcoin-orange text-white shadow-md'
                                : 'text-gray-600 hover:bg-bitcoin-orange hover:text-white hover:shadow-md'
                            }`
                          }
                        >
                          <item.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{item.label}</span>
                          {item.premium && (
                            <span className={`ml-auto px-2 py-0.5 text-xs font-bold text-white rounded-full shadow-sm ${item.proMax ? '' : 'bg-bitcoin-orange'}`}
                              style={item.proMax ? { backgroundColor: '#DD524C' } : {}}>
                              {item.proMax ? 'PRO MAX' : 'PRO'}
                            </span>
                          )}
                        </NavLink>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>

          {/* Learning & Community */}
          <li className="mt-4 pt-4 border-t border-gray-200">
            <ul className="space-y-1">
              {navigationStructure.learning.map((item) => (
                <li key={item.path} className="group relative">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-bitcoin-orange text-white shadow-md'
                          : 'text-gray-600 hover:bg-bitcoin-orange hover:text-white hover:shadow-md'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </nav>

      {/* Bottom Section - About Us & Settings */}
      <div className="p-4 space-y-4 flex-shrink-0">
        {/* About Era Section - Collapsible */}
        <div className="bg-orange-50 rounded-lg border border-orange-200 shadow-sm backdrop-blur-sm bg-opacity-95">
          <button
            onClick={() => setAboutEraVisible(!aboutEraVisible)}
            className="w-full flex items-center justify-between p-3 text-left hover:bg-orange-100 transition-colors rounded-lg"
          >
            <h3 className="text-gray-900 text-sm font-semibold">About Era</h3>
            {aboutEraVisible ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
          </button>
          {aboutEraVisible && (
            <div className="px-3 pb-3">
              <p className="text-gray-600 text-xs leading-relaxed mb-2">
                Founded by passionate investors, Era bridges the gap between traditional and digital assets, 
                making sophisticated market analysis accessible to all aspiring investors.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-bitcoin-orange text-xs font-medium">You're the pilot, Era is your co-Pilot</span>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-gray-500 text-xs">Live</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;